import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  type InfiniteData,
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";

import { api, getApiError } from "@/lib/api";
import {
  type Media,
  type Paginate,
  type SearchRequest,
  type User,
} from "@/types";
import { me } from "@/user/services/me";
import { getNextPaginatePage, notify } from "@/utils";

import {
  type Message,
  type MessengerMessageType,
  type ReplyToMessage,
} from "../types";
import {
  conversations,
  fetchConversationByIdQueryOptions,
  upsertConversationInLists,
} from "./conversation";

export type MessageList = Paginate<Message<true>>;

export const messages = createQueryKeys("messenger-messages", {
  allInfinite: (conversationId?: number, filter?: SearchRequest) => [
    { conversationId, filter },
  ],
  one: (id?: number) => [id],
});

const include = "user";

export const fetchConversationMessages = (
  conversationId: number,
  search?: SearchRequest,
  page: number = 1,
) =>
  api
    .get<MessageList>(`/messenger/conversations/${conversationId}/messages`, {
      params: { page, include, ...search },
    })
    .then((response) => response.data);

export const fetchConversationMessagesQueryOptions = (
  conversationId: number,
  search?: SearchRequest,
) =>
  infiniteQueryOptions({
    ...messages.allInfinite(conversationId, search),
    queryFn: ({ pageParam }) =>
      fetchConversationMessages(conversationId, search, pageParam),
    initialPageParam: 1,
    getNextPageParam: getNextPaginatePage,
  });

export const useFetchConversationMessages = (
  conversationId: number,
  search?: SearchRequest,
  enabled: boolean = true,
) =>
  useInfiniteQuery({
    ...fetchConversationMessagesQueryOptions(conversationId, search),
    enabled,
  });

export const fetchMessageQueryOptions = (id?: number) =>
  queryOptions({
    ...messages.one(id),
    queryFn: () =>
      api
        .get<Message>(`/messenger/messages/${id}`)
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchMessage = (id?: number) =>
  useQuery(fetchMessageQueryOptions(id));

export interface MessageRequest {
  content?: string;
  reply_to?: number;
  file?: string;
  type: MessengerMessageType;
}

function reportSendError(error: unknown) {
  if (!axios.isAxiosError(error) || error.response?.status !== 422) return;
  const message = getApiError(error);
  if (message) notify.error(message);
}

export const useSendMessageToConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: MessageRequest;
      file?: Media;
      replyTo?: ReplyToMessage | null;
    }) =>
      api
        .post(`/messenger/conversations/${id}/messages`, data)
        .then((response) => response.data),
    onMutate: async ({ id, data, file, replyTo }) => {
      await queryClient.cancelQueries({
        queryKey: messages.allInfinite(id).queryKey,
      });

      const currentUser = queryClient.getQueryData<User<true, true>>(me._def);

      const tempId = -Date.now();
      const optimisticMessage: Message<true> = {
        id: tempId,
        content: data.content ?? "",
        type: data.type,
        ...(file && { file }),
        reply_to: replyTo ?? null,
        messenger_conversation_id: id,
        user_id: currentUser?.id ?? 0,
        user: (currentUser ?? {}) as User<true>,
        created_at: new Date().toISOString(),
        status: "pending",
      };

      queryClient.setQueryData<InfiniteData<MessageList>>(
        messages.allInfinite(id).queryKey,
        (old) => {
          if (!old?.pages?.[0]) return old;
          return {
            ...old,
            pages: [
              {
                ...old.pages[0],
                data: [optimisticMessage, ...old.pages[0].data],
              },
              ...old.pages.slice(1),
            ],
          };
        },
      );

      return { tempId };
    },
    onError: (error, { id }, context) => {
      const rejected =
        axios.isAxiosError(error) && error.response?.status === 422;
      reportSendError(error);

      queryClient.setQueryData<InfiniteData<MessageList>>(
        messages.allInfinite(id).queryKey,
        (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: rejected
                ? page.data.filter((msg) => msg.id !== context?.tempId)
                : page.data.map((msg) =>
                    msg.id === context?.tempId
                      ? { ...msg, status: "error" as const }
                      : msg,
                  ),
            })),
          };
        },
      );
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: messages.allInfinite._def,
      });
      queryClient.invalidateQueries({
        queryKey: conversations.allInfinite._def,
      });

      return queryClient.fetchQuery(messages.allInfinite(id));
    },
  });
};

export const useSendMessageToUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: MessageRequest }) =>
      api
        .post<Message<true>>(`/messenger/users/${id}/messages`, data)
        .then((response) => response.data),
    onError: reportSendError,
    onSuccess: async (message, { id }) => {
      queryClient.invalidateQueries({ queryKey: messages.allInfinite._def });
      queryClient.invalidateQueries(conversations.user(id));

      if (!message.messenger_conversation_id) return;

      queryClient.invalidateQueries(
        conversations.one(message.messenger_conversation_id),
      );
      queryClient.invalidateQueries({
        queryKey: messages.allInfinite(message.messenger_conversation_id)
          .queryKey,
      });

      const conversation = await queryClient.fetchQuery(
        fetchConversationByIdQueryOptions(message.messenger_conversation_id),
      );
      if (!conversation) return;

      upsertConversationInLists(queryClient, {
        ...conversation,
        unread_messages_count: 0,
      });
    },
  });
};

export const useDeleteMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`messenger/messages/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: messages.allInfinite._def,
      });
      queryClient.invalidateQueries(messages.one(id));
      queryClient.invalidateQueries({
        queryKey: conversations.allInfinite._def,
      });
    },
  });
};
